import { ArrowDown2, Monitor, Moon, Sun1 } from 'iconsax-reactjs';
import { Avatar, IconButton, Menu, cx, type MenuItem } from '@/components/ui';
import { useOptionalTheme } from '@/components/theme';
import { useThemeMenuItems } from './useThemeMenuItems';
import styles from './UserMenu.module.css';

export interface UserMenuProps {
  name: string;
  /** Role and/or organization, e.g. "Principal · Lincoln Elementary". */
  subtitle?: string;
  avatarSrc?: string | null;
  /** Menu items (profile, security & sessions, theme, sign out...). */
  items: MenuItem[];
  /** Hide name/subtitle text next to the avatar (always hidden on narrow screens). */
  compact?: boolean;
  className?: string;
}

/** Account menu trigger (avatar + name + subtitle) with items supplied by the app. */
export function UserMenu({
  name,
  subtitle,
  avatarSrc,
  items,
  compact = false,
  className,
}: UserMenuProps) {
  return (
    <Menu
      items={items}
      align="end"
      menuLabel={`Account menu for ${name}`}
      renderTrigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label={`Account menu for ${name}`}
          className={cx(styles.trigger, compact && styles.compact, className)}
        >
          <Avatar name={name} src={avatarSrc ?? null} size="sm" decorative />
          <span className={styles.text} aria-hidden="true">
            <span className={styles.name}>{name}</span>
            {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
          </span>
          <ArrowDown2
            size={14}
            color="currentColor"
            className={styles.chevron}
            aria-hidden="true"
          />
        </button>
      )}
    />
  );
}

/** Icon button in the TopBar that switches light / dark / system theme. */
export function ThemeToggle({ className }: { className?: string }) {
  const theme = useOptionalTheme();
  const items = useThemeMenuItems();
  if (!theme) return null;
  const Icon =
    theme.preference === 'system' ? Monitor : theme.resolvedTheme === 'dark' ? Moon : Sun1;
  return (
    <Menu
      items={items}
      align="end"
      menuLabel="Theme"
      renderTrigger={(props) => (
        <IconButton
          {...props}
          className={className}
          label="Theme"
          icon={<Icon size={20} color="currentColor" />}
        />
      )}
    />
  );
}
